import React from 'react';
import { motion } from 'framer-motion';
import { ShoppingBag } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import { SectionHeader } from './shared/SectionHeader';
import { formatCurrencyCompact, formatStat } from '../utils/formatters';

const DONUT_COLORS = ['#3B82F6', '#6366F1', '#10B981', '#F59E0B', '#EC4899', '#8B5CF6'];

export function StoreFunnel({ funnelData, topProducts, categoryPerformance, isMarketer }) {
  if (isMarketer) return null;

  const steps = funnelData?.steps || [];
  const summary = funnelData?.summary || {};
  const maxValue = Math.max(...steps.map(s => s.value || 0), 1);
  const products = (topProducts || []).slice(0, 5);
  const categories = (categoryPerformance || []).slice(0, 5);

  return (
    <div className="mb-6">
      <SectionHeader title="Store & Funnel" icon={ShoppingBag} />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Funnel */}
        <div className="glass-card rounded-2xl p-5">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4">Conversion Funnel</h3>
          <div className="space-y-3">
            {steps.map((step, i) => {
              const pct = maxValue > 0 ? (step.value / maxValue) * 100 : 0;
              return (
                <div key={i}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-medium text-gray-700">{step.name}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold text-gray-900">{formatStat(step.value)}</span>
                      {i > 0 && step.dropoff > 0 && (
                        <span className="text-[10px] text-red-500 font-medium">-{step.dropoff}%</span>
                      )}
                    </div>
                  </div>
                  <div className="h-3 rounded-full bg-gray-100 overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${pct}%` }}
                      transition={{ duration: 0.6, delay: i * 0.1 }}
                      className="h-full rounded-full"
                      style={{ background: `linear-gradient(90deg, #3B82F6, #6366F1)` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>

          {/* Summary cards */}
          <div className="grid grid-cols-3 gap-3 mt-5 pt-4 border-t border-gray-100">
            <div className="text-center">
              <p className="text-[10px] text-gray-400 uppercase tracking-wider">CR</p>
              <p className="text-lg font-bold text-gray-900">{summary.conversionRate || 0}%</p>
            </div>
            <div className="text-center">
              <p className="text-[10px] text-gray-400 uppercase tracking-wider">Drop-off</p>
              <p className="text-lg font-bold text-red-500">{summary.dropoffRate || 0}%</p>
            </div>
            <div className="text-center">
              <p className="text-[10px] text-gray-400 uppercase tracking-wider">AOV</p>
              <p className="text-lg font-bold text-gray-900">{formatCurrencyCompact(summary.avgOrderValue || 0)}</p>
            </div>
          </div>
        </div>

        {/* Products + Category donut */}
        <div className="glass-card rounded-2xl p-5">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4">Top Products</h3>
          <div className="space-y-2.5 mb-5">
            {products.map((p, i) => (
              <div key={i} className="flex items-center gap-3">
                <span className="w-5 h-5 rounded-full bg-blue-50 flex items-center justify-center text-[10px] font-bold text-blue-600 shrink-0">{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-gray-800 truncate">{p.itemName}</p>
                  {p.itemBrand && <p className="text-[10px] text-gray-400 truncate">{p.itemBrand}</p>}
                </div>
                <div className="text-right shrink-0">
                  <p className="text-xs font-bold text-gray-900">{formatCurrencyCompact(p.totalRevenue)}</p>
                  <p className="text-[10px] text-gray-400">{p.totalItemsPurchased} sold</p>
                </div>
              </div>
            ))}
          </div>

          {/* Category donut */}
          {categories.length > 0 && (
            <div className="pt-4 border-t border-gray-100">
              <h4 className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-3">Categories</h4>
              <div className="flex items-center gap-4">
                <div style={{ width: 120, height: 120 }}>
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie
                        data={categories.map(c => ({ name: c.itemCategory, value: c.totalRevenue }))}
                        dataKey="value"
                        cx="50%"
                        cy="50%"
                        innerRadius={30}
                        outerRadius={50}
                        paddingAngle={2}
                      >
                        {categories.map((_, i) => (
                          <Cell key={i} fill={DONUT_COLORS[i % DONUT_COLORS.length]} />
                        ))}
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex-1 space-y-1.5">
                  {categories.map((c, i) => {
                    const shortName = (c.itemCategory || '').split('>').pop().trim() || c.itemCategory;
                    return (
                      <div key={i} className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full shrink-0" style={{ background: DONUT_COLORS[i % DONUT_COLORS.length] }} />
                        <span className="text-[10px] text-gray-600 truncate flex-1">{shortName}</span>
                        <span className="text-[10px] font-semibold text-gray-700">{formatCurrencyCompact(c.totalRevenue)}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
